import { useCallback, useEffect, useMemo } from 'react';

import { usePuppyMode } from '../../../contexts/PuppyModeContext';
import { useSsoPageSearchSuppliersLazyQuery } from '../../../generated';

export interface SsoSupplierOption {
  id: string;
  name: string;
  /** The distributor's short reference, e.g. 00HHA. */
  referenceId: string;
}

export interface UseSsoSupplierSearchResult {
  suppliers: SsoSupplierOption[];
  isSearching: boolean;
  searchSuppliers: (searchTerm: string) => void;
}

/** Keyword search only answers usefully from the third character. */
export const SUPPLIER_SEARCH_MIN_CHARS = 3;

const RESULT_LIMIT = 30;

/**
 * Distributors for the role-mapping form.
 *
 * A first page loads with no keywords, so the list has something in it before
 * anybody types. `keywords` is optional on the backend, so this is the same
 * query rather than a second one. One and two character terms keep that page on
 * screen: the search does not narrow usefully on them, and blanking the list
 * would read as "no distributors".
 */
export const useSsoSupplierSearch = (): UseSsoSupplierSearchResult => {
  const { puppyFilter } = usePuppyMode();
  const [executeSearch, { data, loading }] = useSsoPageSearchSuppliersLazyQuery(
    { fetchPolicy: 'cache-and-network' }
  );

  const runSearch = useCallback(
    (keywords?: string) => {
      executeSearch({
        variables: {
          keywords,
          limit: RESULT_LIMIT,
          ...(puppyFilter !== undefined && {
            filter: { isPuppy: puppyFilter },
          }),
        },
      });
    },
    [executeSearch, puppyFilter]
  );

  useEffect(() => {
    runSearch();
  }, [runSearch]);

  const searchSuppliers = useCallback(
    (searchTerm: string) => {
      const keywords = searchTerm.trim();

      if (keywords.length === 0) {
        runSearch();
        return;
      }

      if (keywords.length < SUPPLIER_SEARCH_MIN_CHARS) {
        return;
      }

      runSearch(keywords);
    },
    [runSearch]
  );

  const suppliers = useMemo(() => {
    const search = data?.supplierSearch;

    // Narrowed on `edges` rather than `__typename`: codegen types the latter as
    // optional on every member of the union, so it discriminates neither arm.
    if (!search || !('edges' in search)) {
      return [];
    }

    return search.edges.map((edge) => ({
      id: edge.node.id,
      name: edge.node.name,
      referenceId: edge.node.referenceId,
    }));
  }, [data]);

  return { suppliers, isSearching: loading, searchSuppliers };
};
